import {
  useCallback, useEffect, useState
} from 'react';

import { APIClient, AuthClient } from '../client';

export * from './useFormData';
export * from './useScrollToTop';

export function useSignup() {
  const [ loading, setLoading ] = useState(false);

  const handleSignup = useCallback(async (payload) => {
    const client = new AuthClient();

    setLoading(true);

    const json = client.signUp(payload);

    setLoading(false);

    return json;
  }, []);

  return {
    handleSignup,
    loading
  };
}

export function useLogin() {
  const [ loading, setLoading ] = useState(false);

  const handleLogin = useCallback(async (payload) => {
    const client = new AuthClient();

    setLoading(true);

    try {
      const json = client.login(payload);

      setLoading(false);

      return json;
    } catch (error) {
      console.error('Error logging in', error);

      setLoading(false);

      return null;
    }
  }, []);

  return {
    handleLogin,
    loading
  };
}

export function useLogout() {
  const [ loading, setLoading ] = useState(false);

  const handleLogout = useCallback(async () => {
    const client = new AuthClient();

    setLoading(true);

    try {
      const json = client.logout();

      setLoading(false);

      return json;
    } catch (error) {
      console.error('Error logging out', error);

      setLoading(false);

      return null;
    }
  }, []);

  return {
    handleLogout,
    loading
  };
}

export function useAuth(): { loading: boolean; user: any; setUser: (user: any) => void } {
  const [ loading, setLoading ] = useState(true);
  const [ user, setUser ] = useState();

  console.log('useAuth', user);

  useEffect(() => {
    const getUser = async () => {
      const client = new AuthClient();
      setLoading(true);

      try {
        console.log('getting session');
        const user = await client.getSession();
        console.log('setting session', user);
        setLoading(false);
        setUser(user ?? null);
      } catch (error) {
        console.error('Error checking auth', error);
        setLoading(false);
        setUser(null);
      }
    };

    getUser();
  }, []);

  return {
    loading,
    setUser,
    user
  };
}

export function useCreate(resource: string) {
  const [ loading, setLoading ] = useState(false);

  const handleCreate = useCallback(
    async (payload: Record<string, any>) => {
      const client = new APIClient(`api/${resource}`);
      console.log('Creating', resource, payload);

      setLoading(true);

      try {
        const json = await client.create(payload);

        setLoading(false);
        console.log('Created', resource, json);

        return json;
      } catch (error) {
        console.error('Error creating', error);

        setLoading(false);

        return null;
      }
    },
    [ resource ]
  );

  return {
    handleCreate,
    loading
  };
}

export function useCreateOrUpdate(resource: string) {
  const [ loading, setLoading ] = useState(false);
  
  const handleCreateOrUpdate = useCallback(async ( id: string,payload: Record<string, string | number | boolean | null | undefined>) => {
    const client = new APIClient(`api/${resource}`);
    setLoading(true);

    let json;
    
    if (id) {
      json = await client.update(id, payload);
    } else {
      json = await client.create(payload);
    }
    
    setLoading(false);
    return json;
  }, [ resource ]);

  return {
    handleCreateOrUpdate,
    loading
  };
}

export function useIndex(resource: string, query = {}, skip = false) {
  const [ loading, setLoading ] = useState(true);
  const [ data, setData ] = useState([]);
  const [ idx, setIdx ] = useState(0);
  console.log(resource, query);

  const queryString = objectToQueryString(query);

  const update = useCallback(() => {
    setIdx(idx + 1);
  }, [ idx ]);

  useEffect(() => {
    if (skip) {
      console.log('Skipping data fetch for', resource);
      setLoading(false);
      return;
    }

    const getData = async () => {
      const client = new APIClient(`api/${resource}?${queryString}`);

      setLoading(true);

      try {
        const json = await client.index();

        setLoading(false);

        setData(json);
      } catch (error) {
        console.error('Error fetching data', error);

        setLoading(false);
      }
    };

    getData();
  }, [ idx, queryString, resource, skip ]);

  return {
    data,
    loading,
    update
  };
}

export function useShow(resource: string) {
  const [ loading, setLoading ] = useState(true);
  const [ entity, setEntity ] = useState(null);

  const handleShow = useCallback(async (id) => {
    console.log('handle show', resource, id);
    setLoading(true);
    const client = new APIClient(`api/${resource}`);
    
    try {
      const json = await client.show(id);
      console.log('Fetched entity:', resource, id, json);
      setEntity(json);
    } catch (error) {
      console.error('Error fetching data', resource, error);
    }
    
    setLoading(false);
  }, [ resource ]);

  return {
    entity,
    handleShow,
    loading
  };
}

export function useUpdate(resource: string) {
  const [ loading, setLoading ] = useState(false);

  const handleUpdate = useCallback(async (id, payload) => {
    const client = new APIClient(`api/${resource}`);

    setLoading(true);

    try {
      const json = await client.update(id, payload);

      setLoading(false);

      return json;
    } catch (error) {
      console.error('Error updating', error);

      setLoading(false);

      return null;
    }
  }, [ resource ]);

  return {
    handleUpdate,
    loading
  };
}

export function useDelete(resource: string) {
  const [ loading, setLoading ] = useState(false);

  const handleDelete = useCallback(async (id: string) => {
    setLoading(true);
    const client = new APIClient(`api/${resource}`);
    try {
      const json = await client.destroy(id);
      setLoading(false);
      return json;
    } catch (error) {
      console.error('Error deleting', error);
      setLoading(false);
      return null;
    }
  }, [ resource ]);

  return {
    handleDelete,
    loading
  };
}

export function objectToQueryString(query: Record<string, any>): string {
  const keys = Object.keys(query).sort();
  const queryString = keys
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
    .join('&');
  return queryString;
}

export function useLocalStorage(key, initialValue) {
  // Get initial value from localStorage or use provided initial value
  const [ storedValue, setStoredValue ] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  const removeValue = () => {
    try {
      localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  };

  return [ storedValue, setValue, removeValue ];
}